// Chat "Perguntar à IA" sobre um cliente (dashboard da Análise de Clientes).
// Painel lateral; cada pergunta vai a /api/reunioes/analise-clientes/perguntar/ com as últimas
// trocas, e o Gemini responde só com base nas reuniões analisadas do cliente.
// A conversa vive só na tela (some ao fechar ou trocar de cliente).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE;
const SUGESTOES = [
  'Quais os principais riscos deste cliente e o que devemos fazer?',
  'Que oportunidades de venda ou expansão apareceram?',
  'Como a relação com o cliente evoluiu nas últimas reuniões?',
  'Quais compromissos combinamos e ainda estão em aberto?',
  'Prepare um roteiro para a próxima reunião com este cliente.',
];

// markdown mínimo da resposta: **negrito** e listas com "-" ou "*"
function Negrito({ texto }) {
  const partes = String(texto).split(/(\*\*[^*]+\*\*)/g);
  return partes.map((p, i) => (p.startsWith('**') && p.endsWith('**')
    ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>));
}

function Resposta({ texto }) {
  const blocos = [];
  let lista = null;
  String(texto).split('\n').forEach((linha, i) => {
    const m = linha.match(/^\s*[-*•]\s+(.*)$/);
    if (m) {
      if (!lista) { lista = []; blocos.push({ tipo: 'ul', itens: lista, k: i }); }
      lista.push(m[1]);
    } else {
      lista = null;
      if (linha.trim()) blocos.push({ tipo: 'p', texto: linha.replace(/^#+\s*/, ''), k: i });
    }
  });
  return blocos.map((b) => (b.tipo === 'ul' ? (
    <ul key={b.k} className="ps-3 mb-2">{b.itens.map((t, j) => <li key={j} className="mb-1"><Negrito texto={t} /></li>)}</ul>
  ) : <p key={b.k} className="mb-2"><Negrito texto={b.texto} /></p>));
}

export default function MemoraClienteChat({ cliente, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const fimRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setMsgs([]); setTexto(''); }, [cliente]);
  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, enviando]);
  useEffect(() => {
    inputRef.current?.focus();
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const perguntar = async (pergunta) => {
    const p = String(pergunta || '').trim();
    if (!p || enviando) return;
    const historico = msgs.filter((m) => !m.erro).map((m) => ({ papel: m.papel, texto: m.texto }));
    setMsgs((m) => [...m, { papel: 'usuario', texto: p }]);
    setTexto('');
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/api/reunioes/analise-clientes/perguntar/`,
        { cliente, pergunta: p, historico },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });
      setMsgs((m) => [...m, { papel: 'ia', texto: r.data?.resposta || 'Sem resposta.' }]);
    } catch (e) {
      setMsgs((m) => [...m, { papel: 'ia', erro: true, texto: e.response?.data?.erro || 'Não foi possível falar com a IA.' }]);
    } finally {
      setEnviando(false);
      inputRef.current?.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); perguntar(texto); }
  };

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1060 }} />
      <aside role="dialog" aria-label={`Perguntar à IA sobre ${cliente}`}
        className="bg-white shadow d-flex flex-column"
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(460px, 100vw)', zIndex: 1061 }}>
        <div className="d-flex justify-content-between align-items-start p-3 border-bottom">
          <div>
            <h6 className="fw-bold mb-0" style={{ color: '#212529' }}><i className="fas fa-comments me-2 text-muted"></i>Perguntar à IA</h6>
            <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>{cliente} · responde com base nas reuniões analisadas</div>
          </div>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
        </div>

        <div className="flex-grow-1 p-3" style={{ overflowY: 'auto', fontSize: '0.88rem' }}>
          {msgs.length === 0 && (
            <div>
              <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>Sugestões:</div>
              <div className="d-flex flex-column gap-2">
                {SUGESTOES.map((s) => (
                  <button key={s} type="button" className="btn btn-sm btn-outline-secondary text-start" onClick={() => perguntar(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`d-flex mb-3 ${m.papel === 'usuario' ? 'justify-content-end' : ''}`}>
              <div className={`rounded p-2 ${m.papel === 'usuario' ? 'bg-light border' : m.erro ? 'border border-danger text-danger' : 'border'}`}
                style={{ maxWidth: '92%', color: m.erro ? undefined : '#212529', lineHeight: 1.5 }}>
                {m.papel === 'ia' && !m.erro ? <Resposta texto={m.texto} /> : m.texto}
              </div>
            </div>
          ))}
          {enviando && (
            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
              <span className="spinner-border spinner-border-sm me-2"></span>Analisando as reuniões do cliente...
            </div>
          )}
          <div ref={fimRef} />
        </div>

        <div className="p-3 border-top">
          <div className="d-flex gap-2">
            <textarea ref={inputRef} className="form-control form-control-sm" rows={2} maxLength={1000}
              placeholder="Pergunte algo sobre este cliente... (Enter envia)"
              value={texto} onChange={(e) => setTexto(e.target.value)} onKeyDown={onKey} disabled={enviando} />
            <button type="button" className="btn btn-primary btn-sm px-3" onClick={() => perguntar(texto)}
              disabled={enviando || !texto.trim()} title="Enviar">
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
          <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>A IA pode errar — confira na reunião citada antes de agir.</div>
        </div>
      </aside>
    </>,
    document.body
  );
}
